// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type Holding } from '@shared/data-access/interfaces/api/ai/holding';
import { useCallback, useMemo } from 'react';
import { useMultiAssetBacktest } from '@shared/portfolio-analytics/components/backtest';
import { isTest } from '@shared/environment';
import { isDefined } from '@shared/utils';
import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';

export interface UseHoldingsBacktestArgs {
  holdings: Pick<
    AccountsHoldingsData,
    'details' | 'ticker' | 'isin' | 'price' | 'quantity'
  >[];
  hist: number;
  benchmark: Array<{ ticker: string; weight: number }>;
}

export const useHoldingsBacktest = ({
  hist,
  holdings: positions,
  benchmark,
}: UseHoldingsBacktestArgs) => {
  /* istanbul ignore next */
  const holdings = useMemo<Holding[]>(
    () =>
      positions
        .filter(({ details }) => isDefined(details))
        .map(
          (holding): Holding => ({
            ticker:
              holding.details?.ticker ??
              holding.details?.isin ??
              holding.ticker ??
              holding.isin!,
            price: holding.price,
            qty: holding.quantity,
            unrealized_capital_gains: 0,
          }),
        ),
    [positions],
  );

  const {
    analysis,
    backtest,
    kpis,
    returns,
    isError,
    isFetchingBacktesting,
    isLoadingBacktesting,
    refetchAnalysis,
    refetchBacktest,
  } = useMultiAssetBacktest({
    benchmark,
    holdings,
    hist,
    skip: isTest ? false : benchmark.length === 0 || holdings.length === 0,
  });
  /* istanbul ignore next */
  const onRefresh = useCallback(() => {
    refetchAnalysis();
    refetchBacktest();
  }, [refetchAnalysis, refetchBacktest]);

  return {
    isLoadingBacktesting,
    isFetchingBacktesting,
    analysis,
    backtest,
    kpis,
    returns,
    isError,
    onRefresh,
  } as const;
};
